import React, { useState, useRef, useEffect } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import SignatureModal from '../modals/SignatureModal';
import ViewRejectionDetailsModal from '../modals/ViewRejectionModal';
import ViewVerificationDetailsModal from '../modals/ViewVerificationDetailsModal';
import AddressPromptModal from '../dashboard/AddressPromptModal';

const AccountSettings = ({ user }) => {
  const { refreshUserData } = useAuth();
  const [formData, setFormData] = useState({
    fullname: user?.fullname || '',
    username: user?.username || '',
    email: user?.company_email || '',
    website: user?.company_website || '',
    reg_number: user?.reg_number || '',
    company: user?.company_name || '',
    currency: user?.currency || '',
    phonenumber: user?.company_phone || '',
    call_back_url: user?.callback_url || '',
    address: user?.company_address || '',
    location_coord: user?.location_coord || ''
  });
  const [businessdata, setBusinessData] = useState({})

  const [originalData, setOriginalData] = useState({});
  const [validationStatus, setValidationStatus] = useState({
    company: { status: '', message: '', checking: false },
    username: { status: '', message: '', checking: false }
  });
  const [isBusinessRegistered, setIsBusinessRegistered] = useState(false);
  const [requiresBusinessVerification, setRequiresBusinessVerification] = useState(false);
  const [verificationData, setVerificationData] = useState({
    rc_number: '',
    business_certificate: null,
    ownsBusiness: false
  });
  const [filePreview, setFilePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState({
    profile: false,
    signature: false
  });
  const [profilePreview, setProfilePreview] = useState(user?.company_logo || '');
  const [signaturePreview, setSignaturePreview] = useState(user?.company_signature || '');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showLogoPreview, setShowLogoPreview] = useState(false);
  const [tempLogo, setTempLogo] = useState(null);

  const profileInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const validationTimeout = useRef(null);

  // Add this state to your existing states
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Phone number OTP verification flow
  const [phoneStep, setPhoneStep] = useState('idle'); // idle | sending | otp-sent | verifying | success
  const [newPhoneInput, setNewPhoneInput] = useState('');
  const [newCountryCode, setNewCountryCode] = useState('+234');
  const [phoneOtpDigits, setPhoneOtpDigits] = useState(['', '', '', '', '', '']);
  const [phoneResendCooldown, setPhoneResendCooldown] = useState(0);
  const [phoneError, setPhoneError] = useState('');
  const phoneOtpInputRefs = useRef([]);
  const phoneInputRef = useRef(null);
  const [phoneShake, setPhoneShake] = useState(false);

  // Keep the displayed address in sync when the saved one changes (the address
  // is edited via the map modal, not this form).
  useEffect(() => {
    setFormData(prev => ({ ...prev, address: user?.company_address || '' }));
  }, [user?.company_address]);

  // Phone OTP resend cooldown timer
  useEffect(() => {
    if (phoneResendCooldown <= 0) return;
    const timer = setInterval(() => {
      setPhoneResendCooldown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [phoneResendCooldown]);

  // Phone OTP input ref initializer
  useEffect(() => {
    phoneOtpInputRefs.current = phoneOtpInputRefs.current.slice(0, 6);
  }, []);

  const handlePhoneOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...phoneOtpDigits];
    newOtp[index] = value;
    setPhoneOtpDigits(newOtp);
    if (value && index < 5) {
      phoneOtpInputRefs.current[index + 1]?.focus();
    }
    setPhoneError('');
  };

  const handlePhoneOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;
    const newOtp = [...phoneOtpDigits];
    for (let i = 0; i < pastedData.length; i++) {
      if (i < 6) newOtp[i] = pastedData[i];
    }
    setPhoneOtpDigits(newOtp);
    const lastFilled = Math.min(pastedData.length - 1, 5);
    phoneOtpInputRefs.current[lastFilled]?.focus();
  };

  const handlePhoneOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !phoneOtpDigits[index] && index > 0) {
      phoneOtpInputRefs.current[index - 1]?.focus();
    }
  };

  const resetPhoneFlow = () => {
    setPhoneStep('idle');
    setNewPhoneInput('');
    setPhoneOtpDigits(['', '', '', '', '', '']);
    setPhoneResendCooldown(0);
    setPhoneError('');
  };

  const triggerPhoneShake = () => {
    setPhoneShake(true);
    setTimeout(() => setPhoneShake(false), 500);
  };

  const handleSendPhoneOtp = async () => {
    if (!newPhoneInput.trim()) {
      setPhoneError('Phone number is required');
      triggerPhoneShake();
      return;
    }
    setPhoneStep('sending');
    setPhoneError('');
    try {
      const response = await api.post(API_CONFIG.AUTH.VERIFY_PHONE, {
        countryCode: newCountryCode,
        phoneNumber: newPhoneInput,
        userId: user?.id
      });
      const data = await response.data;
      if (data.error) {
        setPhoneError(data.message || data.error);
        setPhoneStep('entering');
        triggerPhoneShake();
      } else {
        showToast('OTP sent to your WhatsApp', 'success');
        setPhoneStep('otp-sent');
        setPhoneResendCooldown(60);
      }
    } catch (error) {
      setPhoneError(error.response?.data?.message || 'Failed to send OTP. Please try again.');
      setPhoneStep('entering');
      triggerPhoneShake();
    }
  };

  const handleResendPhoneOtp = async () => {
    if (phoneResendCooldown > 0) return;
    setPhoneError('');
    try {
      await api.post(API_CONFIG.AUTH.RESEND_VERIFICATION_OTP, {
        countryCode: newCountryCode,
        phoneNumber: newPhoneInput,
        userId: user?.id
      });
      showToast('OTP resent successfully', 'success');
      setPhoneResendCooldown(60);
    } catch (error) {
      setPhoneError('Failed to resend OTP. Please try again.');
    }
  };

  const handleVerifyPhoneOtp = async () => {
    const otpString = phoneOtpDigits.join('');
    if (!otpString || otpString.length !== 6) {
      setPhoneError('Please enter the 6-digit OTP');
      return;
    }
    setPhoneStep('verifying');
    setPhoneError('');
    try {
      const verifyRes = await api.post(API_CONFIG.AUTH.VERIFY_OTP, {
        countryCode: newCountryCode,
        phoneNumber: newPhoneInput,
        otp: otpString,
        userId: user?.id
      });
      const verifyData = await verifyRes.data;
      if (verifyData.error) {
        setPhoneError(verifyData.error || verifyData.message || 'Invalid OTP. Please try again.');
        setPhoneStep('otp-sent');
        return;
      }
      // OTP verified — now update the phone number on the account
      await api.post(API_CONFIG.USER.UPDATE_ACCOUNT_SETTINGS, {
        phonenumber: newPhoneInput
      });
      showToast('Phone number updated successfully', 'success');
      setPhoneStep('success');
      await refreshUserData();
      setTimeout(() => resetPhoneFlow(), 2000);
    } catch (error) {
      setPhoneError(error.response?.data?.error || 'Verification failed. Please try again.');
      setPhoneStep('otp-sent');
    }
  };
  const fetchBusinessData = async (businessName, userId) => {
    try {
      // Set loading state if you have one
  

      // Validate inputs
      if (!businessName?.trim() || !userId) {
        console.error("Valid business name and user ID are required");
        setBusinessData(null);
    
        return;
      }

      const response = await api.post(API_CONFIG.USER.GET_BUSINESS_VERIFICATION, {
        businessName: businessName.trim(),
        userId
      });

      const data = response.data;

      if (data.success) {
        if (data.businessData) {
          setBusinessData(data.businessData);
          console.log("Business data loaded successfully:");
        } else {
          console.log("No business verification data available");
          setBusinessData(null);
        }
      } else {
        console.error("Error fetching business data:", data.error || data.message);
        setBusinessData(null);

        // You can show user-friendly messages based on error code
        if (data.code === "NO_DATA_FOUND") {
          // Handle no data found scenario
        }
      }

    } catch (error) {
      console.error("Error fetching business verification data:", error);

      // Enhanced error handling
      let errorMessage = "Failed to fetch business data";

      if (error.response) {
        const serverError = error.response.data;
        errorMessage = serverError.error || serverError.message || errorMessage;
        console.error("Server response error:", serverError);
      } else if (error.request) {
        errorMessage = "Network error: Unable to connect to server";
        console.error("Network error:", error.request);
      } else {
        errorMessage = error.message;
      }

      // You can show this error to the user
      console.error("Business data fetch error:", errorMessage);
      setBusinessData(null);

    } finally {
      setIsLoading(false);
    }
  }


// Use useEffect to call the function when conditions are met
useEffect(() => {
  if (user?.requested_name_change > 0 && user?.requested_name && user?.id) {
    fetchBusinessData(user.requested_name, user.id);
  }
}, [user?.requested_name_change, user?.requested_name, user?.id]); // Dependencies array

// Or if you only want to fetch once when the component mounts and conditions are met:
useEffect(() => {
  if (user?.requested_name_change > 0 && user?.requested_name && user?.id) {
    fetchBusinessData(user.requested_name, user.id);
  }
  // Empty dependency array means this runs once on mount
}, []); 
// Add this function to handle viewing details
  
  const handleViewDetails = async (requestedName, userId, status) => {
    try {
      // The modal will handle the API call when it opens
      if(status === "reject"){
      setShowRejectionModal(true);
      }else{
setShowVerificationModal(true)
      }
    } catch (error) {
      console.error('Error preparing rejection details:', error);
      showToast('Failed to load rejection details', 'error');
    }
  };

  // Add this function to handle reapplication
  const handleReapply = async () => {
    try {
      // You can customize this based on your reapplication logic
      const response = await api.post(API_CONFIG.USER.SUBMIT_REAPPLICATION, {
        userId: user?.id,
        requested_name: user?.requested_name,
        previous_rejection_id: user?.rejection_id // if you have this
      });

      if (response.data.success) {
        showToast('Reapplication submitted successfully!', 'success');
        // You might want to refresh user data or update status
      }
    } catch (error) {
      console.error('Error submitting reapplication:', error);
      throw error; // This will be caught in the modal
    }
  };
  // Initialize original data
  useEffect(() => {
    setOriginalData({
      company: user?.company_name || '',
      username: user?.username || ''
    });
  }, [user]);

  // Check if form has changes
  const hasChanges = () => {
    return (
      formData.company !== originalData.company ||
      formData.username !== originalData.username ||
      formData.fullname !== user?.fullname ||
      formData.email !== user?.company_email ||
      formData.website !== user?.company_website ||
      formData.reg_number !== user?.reg_number ||
      formData.call_back_url !== user?.callback_url ||
      formData.location_coord !== user?.location_coord
    );
  };

  // Check if form is ready for submission
  const isFormReady = () => {
    const hasValidationErrors =
      validationStatus.company.status === 'error' ||
      validationStatus.username.status === 'error';

    const isChecking =
      validationStatus.company.checking ||
      validationStatus.username.checking;

    return hasChanges() && !hasValidationErrors && !isChecking &&
      (!requiresBusinessVerification ||
        (verificationData.ownsBusiness &&
          verificationData.rc_number.trim() &&
          verificationData.business_certificate));
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    // Clear validation status when user starts typing
    if (['company', 'username'].includes(id)) {
      setValidationStatus(prev => ({
        ...prev,
        [id]: { status: '', message: '', checking: false }
      }));
    }

    // Clear existing timeout
    if (validationTimeout.current) {
      clearTimeout(validationTimeout.current);
    }

    // Set timeout for validation
    if (id === 'company' && value.length > 2 && value !== originalData.company) {
      setValidationStatus(prev => ({
        ...prev,
        company: { ...prev.company, checking: true }
      }));
      validationTimeout.current = setTimeout(() => {
        checkCompanyNameAvailability(value);
      }, 1000);
    } else if (id === 'username' && value.length > 2 && value !== originalData.username) {
      setValidationStatus(prev => ({
        ...prev,
        username: { ...prev.username, checking: true }
      }));
      validationTimeout.current = setTimeout(() => {
        checkUsernameAvailability(value);
      }, 800);
    }
  };

  // Check company name availability and registration status
  const checkCompanyNameAvailability = async (companyName) => {
    try {
      const response = await api.post(API_CONFIG.AUTH.IS_REGISTERED_BUSINESS, {
        businessName: companyName,
        userId: user?.id
      });
      const data = await response.data;



      if (data.isRegistered) {
        setValidationStatus(prev => ({
          ...prev,
          company: {
            status: 'registered',
            message: 'This business name is registered. Please verify ownership to update.',
            checking: false
          }
        }));
        setIsBusinessRegistered(true);
        setRequiresBusinessVerification(true);
      } else if (data.isTaken) {
        setValidationStatus(prev => ({
          ...prev,
          company: {
            status: 'error',
            message: 'This company name is already taken.',
            checking: false
          }
        }));
      } else {
        setValidationStatus(prev => ({
          ...prev,
          company: {
            status: 'success',
            message: 'Company name is available.',
            checking: false
          }
        }));
      }
    } catch (error) {
      setValidationStatus(prev => ({
        ...prev,
        company: {
          status: 'error',
          message: 'Error checking company name. Please try again.',
          checking: false
        }
      }));
    }
  };

  // Check username availability
  const checkUsernameAvailability = async (username) => {
    try {
      const response = await api.post(API_CONFIG.USER.VERIFY_USERNAME, {
        username: username,
        userId: user?.company_id
      });
      const data = response.data;

      if (data.isTaken) {
        setValidationStatus(prev => ({
          ...prev,
          username: {
            status: 'error',
            message: 'This username is already taken.',
            checking: false
          }
        }));
      } else {
        setValidationStatus(prev => ({
          ...prev,
          username: {
            status: 'success',
            message: 'Username is available.',
            checking: false
          }
        }));
      }
    } catch (error) {
      setValidationStatus(prev => ({
        ...prev,
        username: {
          status: 'error',
          message: 'Error checking username. Please try again.',
          checking: false
        }
      }));
    }
  };

  const handleVerificationChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setVerificationData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setVerificationData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10MB', 'error');
        return;
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showToast('Please upload a PDF, JPG, or PNG file', 'error');
        return;
      }

      setVerificationData(prev => ({
        ...prev,
        business_certificate: file
      }));

      // Create preview
      if (file.type === 'application/pdf') {
        setFilePreview({
          type: 'pdf',
          name: file.name,
          size: file.size
        });
      } else if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview({
            type: 'image',
            url: e.target.result,
            name: file.name,
            size: file.size
          });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const removeFile = () => {
    setVerificationData(prev => ({
      ...prev,
      business_certificate: null
    }));
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };




  const handleProfileUpload = () => {
    profileInputRef.current?.click();
  };

  const handleProfileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (800KB max)
      if (file.size > 5000 * 1024) {
        showToast('File size must be less than 5MB', 'error');
        return;
      }

      // Check file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        showToast('Only JPG, PNG, and GIF files are allowed', 'error');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const previewUrl = e.target.result;
        setTempLogo({ file, previewUrl });
        setShowLogoPreview(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const confirmLogoUpload = async () => {
    if (!tempLogo) return;

    setUploadLoading(prev => ({ ...prev, profile: true }));

    try {
      const formData = new FormData();
      formData.append('profile', tempLogo.file);

      const response = await api.post(API_CONFIG.USER.UPDATE_PROFILE_PICTURE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      if (data.success) {
        showToast('Profile picture updated successfully!', 'success');
        setProfilePreview(tempLogo.previewUrl);
        if (data.url) {
          setProfilePreview(data.url);
        }
      } else {
        showToast(data.error || 'Failed to update profile picture', 'error');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      showToast('An error occurred during profile picture upload', 'error');
    } finally {
      setUploadLoading(prev => ({ ...prev, profile: false }));
      setShowLogoPreview(false);
      setTempLogo(null);
    }
  };

  const cancelLogoUpload = () => {
    setShowLogoPreview(false);
    setTempLogo(null);
    // Reset the file input
    if (profileInputRef.current) {
      profileInputRef.current.value = '';
    }
  };

  const handleSignatureUpload = () => {
    setShowSignatureModal(true);
  };

  const handleSignatureFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleSignatureFile(file);
    }
  };

  const handleSignatureFile = async (file) => {
    // Check file size (800KB max)
    if (file.size > 800 * 1024) {
      showToast('File size must be less than 800KB', 'error');
      return;
    }

    // Check file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      showToast('Only PNG and JPG files are allowed for signatures', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setSignaturePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload the file
    await uploadSignatureFile(file);
  };

  const uploadSignatureFile = async (file) => {
    setUploadLoading(prev => ({ ...prev, signature: true }));

    const formData = new FormData();
    formData.append('signature', file);

    try {
      const response = await api.post(API_CONFIG.USER.UPDATE_SIGNATURE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      if (data.success) {
        showToast('Signature updated successfully!', 'success');
        if (data.url) {
          setSignaturePreview(data.url);
        }
      } else {
        showToast(data.error || 'Failed to update signature', 'error');
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      showToast('An error occurred during signature upload', 'error');
    } finally {
      setUploadLoading(prev => ({ ...prev, signature: false }));
    }
  };

  const uploadFile = async (file, type) => {
    setUploadLoading(prev => ({ ...prev, [type]: true }));

    const formData = new FormData();
    formData.append(type === 'profile' ? 'profile' : 'signature', file);

    try {
      const endpoint = type === 'profile' ? '/updateProfilePicture' : '/updateSignature';
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      if (data.success) {
        showToast(`${type === 'profile' ? 'Profile picture' : 'Signature'} updated successfully!`, 'success');
        if (data.url) {
          if (type === 'profile') {
            setProfilePreview(data.url);
          } else {
            setSignaturePreview(data.url);
          }
        }
      } else {
        showToast(data.error || `Failed to update ${type}`, 'error');
      }
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      showToast(`An error occurred during ${type} upload`, 'error');
    } finally {
      setUploadLoading(prev => ({ ...prev, [type]: false }));
    }
  };


  // Business-verification saves (registered company name change with certificate
  // upload) still go through the legacy multipart endpoint.
  const submitWithBusinessVerification = async () => {
    const submitFormData = new FormData();

    submitFormData.append('fullname', formData.fullname);
    submitFormData.append('username', formData.username);
    submitFormData.append('email', formData.email);
    submitFormData.append('website', formData.website || '');
    submitFormData.append('reg_number', formData.reg_number || '');
    submitFormData.append('company', formData.company);
    submitFormData.append('currency', formData.currency);
    submitFormData.append('call_back_url', formData.call_back_url || '');

    if (verificationData.ownsBusiness) {
      submitFormData.append('rc_number', verificationData.rc_number);
      submitFormData.append('ownsBusiness', 'true');
      if (verificationData.business_certificate) {
        submitFormData.append('business_certificate', verificationData.business_certificate);
      }
      submitFormData.append('company_name_status', 'pending');
    }

    const response = await api.post(API_CONFIG.USER.UPDATE_PERSONAL_DETAILS, submitFormData);
    const data = response.data;

    if (data.success) {
      showToast(data.success, 'success');
      setOriginalData({
        company: formData.company,
        username: formData.username,
        phonenumber: formData.phonenumber
      });
      if (formData.company !== originalData.company) {
        setRequiresBusinessVerification(false);
        setVerificationData({ rc_number: '', business_certificate: null, ownsBusiness: false });
        setFilePreview(null);
      }
    } else {
      showToast(data.error || 'Failed to update details', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormReady()) {
      showToast('Please complete all validations before saving', 'error');
      return;
    }

    setIsLoading(true);

    try {
      // Registered-business name changes require the certificate upload flow.
      if (requiresBusinessVerification && verificationData.ownsBusiness) {
        await submitWithBusinessVerification();
        return;
      }

      // Incremental update: send only the fields that actually changed. The
      // backend diffs again and skips anything already stored (username is only
      // touched when it changed).
      const changed = {};
      if (formData.fullname !== user?.fullname) changed.fullname = formData.fullname;
      if (formData.username !== user?.username) changed.username = formData.username;
      if (formData.email !== user?.company_email) changed.email = formData.email;
      if (formData.website !== user?.company_website) changed.website = formData.website || '';
      if (formData.reg_number !== user?.reg_number) changed.reg_number = formData.reg_number || '';
      if (formData.company !== user?.company_name) changed.company = formData.company;
      if (formData.currency !== user?.currency) changed.currency = formData.currency;
      if (formData.call_back_url !== user?.callback_url) changed.call_back_url = formData.call_back_url || '';

      if (Object.keys(changed).length === 0) {
        showToast('No changes to save', 'info');
        return;
      }

      const response = await api.post(API_CONFIG.USER.UPDATE_ACCOUNT_SETTINGS, changed);
      const data = response.data;

      if (data.success) {
        showToast(data.message || 'Profile updated successfully', 'success');
        // Refresh so stored values + change detection stay accurate.
        await refreshUserData();
        setOriginalData({
          company: formData.company,
          username: formData.username
        });
      } else {
        showToast(data.error || 'Failed to update details', 'error');
      }
    } catch (error) {
      console.error('Error updating account settings:', error);
      showToast(error.response?.data?.error || 'An error occurred while updating details', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Status indicator component
  const StatusIndicator = ({ status, checking }) => {
    if (checking) {
      return (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
      );
    }

    switch (status) {
      case 'success':
        return <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>;
      case 'error':
        return <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>;
      case 'registered':
        return <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo Preview Modal */}
      {showLogoPreview && tempLogo && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-sm sm:max-w-md">
            <h3 className="text-xl font-semibold  mb-4 text-center">
              Confirm Logo Upload
            </h3>

            <div className="mb-4 sm:mb-6 text-center">
              <div className="w-36 h-36 sm:w-48 sm:h-48 mx-auto rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-gray-300 mb-4">
                <img
                  src={tempLogo.previewUrl}
                  alt="Logo preview"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-gray-600">Preview of your new logo</p>
            </div>

            <div className="flex gap-3 justify-center">
              <button
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={cancelLogoUpload}
                disabled={uploadLoading.profile}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                onClick={confirmLogoUpload}
                disabled={uploadLoading.profile}
              >
                {uploadLoading.profile ? 'Uploading...' : 'Confirm Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      <SignatureModal
        show={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSignatureUpload={handleSignatureFile}
      />

      {/* Rejection Details Modal */}
      <ViewRejectionDetailsModal
        show={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        userId={user?.id}
        requestedName={user?.requested_name}
        onReapply={handleReapply}
      />

  


      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Logo Upload Card */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold mb-2">Company Logo</h3>
          <p className="text-gray-500 text-sm mb-4">Change your Company Logo from here</p>
          <div className="text-center">
            <div className="mb-4">
              <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                {profilePreview ? (
                  <img
                    src={profilePreview}
                    alt="Company logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>
            </div>
            <input
              ref={profileInputRef}
              type="file"
              accept=".png, .jpg, .jpeg, .gif"
              className="hidden"
              onChange={handleProfileChange}
              disabled={uploadLoading.profile}
            />
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              onClick={handleProfileUpload}
              disabled={uploadLoading.profile}
            >
              {uploadLoading.profile ? 'Uploading...' : 'Upload Logo'}
            </button>
            <p className="text-xs text-gray-500 mt-3">Allowed JPG, GIF or PNG. Max 800KB</p>
          </div>
        </div>

        {/* Signature Upload Card */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold mb-2">Digital Signature</h3>
          <p className="text-gray-500 text-sm mb-4">Upload a new Signature</p>
          <div className="text-center">
            <div className="mb-4">
              <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto bg-white flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 rounded-lg">
                {signaturePreview ? (
                  <img
                    src={signaturePreview}
                    alt="Signature"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                )}
              </div>
            </div>
            <input
              ref={signatureInputRef}
              type="file"
              accept=".png, .jpg, .jpeg"
              className="hidden"
              onChange={handleSignatureFileChange}
              disabled={uploadLoading.signature}
            />
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              onClick={handleSignatureUpload}
              disabled={uploadLoading.signature}
            >
              {uploadLoading.signature ? 'Uploading...' : 'Upload Signature'}
            </button>
            <p className="text-xs text-gray-500 mt-3">Allowed PNG or JPG. Max 800KB</p>
          </div>
        </div>
      </div>

      {/* Personal Details Form */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border border-gray-200">
        <h3 className="text-base sm:text-lg font-semibold mb-2">Personal Details</h3>
        <p className="text-gray-500 text-sm mb-4 sm:mb-6">To change your personal detail, edit and save from here</p>

        <form onSubmit={handleSubmit} encType='multipart/form-data'>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="fullname" className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                <input
                  type="text"
                  id="fullname"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.fullname}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <div className="relative">
                  <input
                    type="text"
                    id="username"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors pr-10 ${validationStatus.username.status === 'success' ? 'border-green-300 bg-green-50' :
                      validationStatus.username.status === 'error' ? 'border-red-300 bg-red-50' :
                        'border-gray-300'
                      }`}
                    value={formData.username}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <div className="absolute right-3 top-2.5">
                    <StatusIndicator
                      status={validationStatus.username.status}
                      checking={validationStatus.username.checking}
                    />
                  </div>
                </div>
                {validationStatus.username.message && (
                  <p className={`text-sm mt-1 ${validationStatus.username.status === 'success' ? 'text-green-600' :
                    validationStatus.username.status === 'error' ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                    {validationStatus.username.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                />
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">Company Website</label>
                <input
                  type="text"
                  id="website"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.website}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </div>

              {/* Company Registration Number - Show as display field when approved */}


              {(user?.name_change_status === 'approved' && user?.requested_name_change > 0) ? (
                <div className="space-y-2">
                  <label htmlFor="reg_number" className="block text-sm font-medium text-gray-700 mb-1">Company Reg. Number</label>
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-900 font-medium">{businessdata?.rc_number || ""}</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Verified
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">Your verified registration number</p>
                </div>
              ) : (
                <> </>
              )}

              {/* Company Registration Number - Show as display field when approved */}
              {(user?.name_change_status === 'rejected' && user?.requested_name_change > 0) ? (
                <div className="space-y-2">
                  <label htmlFor="reg_number" className="block text-sm font-medium text-gray-700 mb-1">Company Reg. Number</label>
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-900 font-medium">{businessdata?.rc_number}</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Rejected
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">Your verification request was rejected</p>
                </div>
              ) : (
                <></>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>

                {/* Business Name Change Status Banner - PENDING */}
                {user?.name_change_status === 'pending' || user?.name_change_status === 'under-review' && user?.requested_name_change > 0 && (
                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-4 sm:p-6 mb-6 shadow-sm">
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      {/* Animated Icon */}
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-yellow-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          {/* Pulsing dot for active status */}
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-ping"></div>
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-600 rounded-full"></div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm sm:text-lg font-semibold text-yellow-900 flex items-center flex-wrap gap-1">
                            Business Name Change Under Review
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full border border-yellow-200">
                              PENDING
                            </span>
                          </h4>
                          <div className="text-yellow-600 text-sm font-medium">
                            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Processing
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-yellow-800 text-sm">
                            Your request to change your business name from
                            <span className="font-semibold mx-1 text-yellow-900">{user.company_name}</span>
                            to
                            <span className="font-semibold mx-1 text-yellow-900">{user.requested_name}</span>
                            is currently being reviewed by our verification team.
                          </p>

                          <div className="flex flex-wrap gap-3 text-xs">
                            <div className="flex items-center text-yellow-700">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Submitted on {new Date(user.name_change_requested_at).toLocaleDateString()}
                            </div>
                            <div className="flex items-center text-yellow-700">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Typically takes 1-2 business days
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-yellow-700 mb-1">
                            <span>Verification Progress</span>
                            <span>Under Review</span>
                          </div>
                          <div className="w-full bg-yellow-200 rounded-full h-2">
                            <div className="bg-yellow-500 h-2 rounded-full w-2/3 animate-pulse"></div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                )}

                {/* Business Name Change Status Banner - APPROVED */}
                {user?.name_change_status === 'approved' && user?.requested_name_change > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 sm:p-6 mb-6 shadow-sm">
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      {/* Success Icon */}
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm sm:text-lg font-semibold text-green-900 flex items-center flex-wrap gap-1">
                            Business Name Change Approved
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full border border-green-200">
                              APPROVED
                            </span>
                          </h4>
                        
                        </div>

                        <div className="space-y-2">
                          <p className="text-green-800 text-sm">
                            Your business name has been successfully changed from
                            <span className="font-semibold mx-1 text-green-900 line-through">{user.company_name}</span>
                            to
                            <span className="font-semibold mx-1 text-green-900">{user.requested_name}</span>.
                          </p>

                          <div className="flex flex-wrap gap-3 text-xs">
                            <div className="flex items-center text-green-700">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Approved on {new Date(user.name_change_approved_at).toLocaleDateString()}
                            </div>
                            <div className="flex items-center text-green-700">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              Updated across all systems
                            </div>
                              <div className="text-green-600  font-medium">
                            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Completed
                          </div>
                          </div>
                        </div>
                      </div>

                        {/* Action Button */}
                      <div className="flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => handleViewDetails(user.requested_name, user.id, "pending")}
                          className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-700 rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Business Name Change Status Banner - REJECTED */}
                {user?.name_change_status === 'rejected' && user?.requested_name_change > 0 && (
                  <div className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-4 sm:p-6 mb-6 shadow-sm">
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm sm:text-lg font-semibold text-red-900 flex items-center flex-wrap gap-1">
                            Business Name Change Rejected
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full border border-red-200">
                              REJECTED
                            </span>
                          </h4>

                        </div>

                        <div className="space-y-2">
                          <p className="text-red-800 text-sm">
                            Your request to change your business name from
                            <span className="font-semibold mx-1 text-red-900 line-through">{user.company_name}</span>
                            to
                            <span className="font-semibold mx-1 text-red-900">{user.requested_name}</span>
                            was not approved.
                          </p>

                          <div className="flex flex-wrap gap-3 text-xs">
                            <div className="flex items-center text-red-700">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Rejected on {new Date(user.name_change_rejected_at).toLocaleDateString()}
                            </div>
                            <div className="text-red-600 text-xsm font-medium">
                              <svg className="w-2 h-2 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Not Approved
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => handleViewDetails(user.requested_name, user.id, "rejected")}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-700 rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Company Name Input Field - Always show input field but display approved name below */}
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="text"
                      id="company"
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 pr-12 ${validationStatus.company.status === 'success'
                        ? 'border-green-400 bg-green-50 focus:ring-green-500 focus:border-green-500'
                        : validationStatus.company.status === 'registered'
                          ? 'border-yellow-400 bg-yellow-50 focus:ring-yellow-500 focus:border-yellow-500'
                          : validationStatus.company.status === 'error'
                            ? 'border-red-400 bg-red-50 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 bg-white focus:ring-blue-500 focus:border-blue-500'
                        } ${isLoading || user?.name_change_status === 'under-review' || user?.name_change_status === 'pending' ? 'opacity-60 cursor-not-allowed' : ''}`}
                      value={formData.company}
                      onChange={handleInputChange}
                      disabled={isLoading || user?.name_change_status === 'under-review' || user?.name_change_status === 'pending'}
                      placeholder="Enter your company name"
                    />

                    {/* Status Indicator - Properly aligned */}
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {validationStatus.company.checking ? (
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      ) : (
                        <StatusIndicator
                          status={validationStatus.company.status}
                          checking={validationStatus.company.checking}
                          size="md"
                        />
                      )}
                    </div>
                  </div>

                  {/* Show current approved name when user hasn't started typing a new name */}
                  {user?.name_change_status === 'approved' &&
                    user?.requested_name_change > 0 &&
                    formData.company === user.requested_name && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-900">Current Approved Name</p>
                            <p className="text-blue-700 text-sm mt-1">{user.requested_name}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                              Verified
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Status Message */}
                  {validationStatus.company.message && (
                    <div className={`flex items-start space-x-2 text-sm ${validationStatus.company.status === 'success' ? 'text-green-600' :
                      validationStatus.company.status === 'registered' ? 'text-yellow-600' :
                        validationStatus.company.status === 'error' ? 'text-red-600' :
                          'text-gray-500'
                      }`}>
                      <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${validationStatus.company.status === 'success' ? 'text-green-500' :
                        validationStatus.company.status === 'registered' ? 'text-yellow-500' :
                          validationStatus.company.status === 'error' ? 'text-red-500' :
                            'text-gray-400'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {validationStatus.company.status === 'success' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                        {validationStatus.company.status === 'registered' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        )}
                        {validationStatus.company.status === 'error' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        )}
                      </svg>
                      <span className="leading-relaxed">{validationStatus.company.message}</span>
                    </div>
                  )}

                  {/* Helper Text */}
                  {!validationStatus.company.message && !(user?.name_change_status === 'approved' && user?.requested_name_change > 0) && (
                    <p className="text-xs text-gray-500">
                      Your registered business name as it appears on official documents
                    </p>
                  )}
                </div>
              </div>


              {/* <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  id="currency"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors bg-gray-100"
                  value={formData.currency}
                  onChange={handleInputChange}
                  disabled
                >
                  <option value={formData.currency}>{formData.currency}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Currency cannot be changed after registration</p>
              </div> */}

            </div>
          </div>

          {/* Business Verification Section */}
          {requiresBusinessVerification && (
            <div className="mt-4 sm:mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-semibold text-yellow-800 text-base sm:text-lg mb-3">Business Verification Required</h4>
              <p className="text-yellow-700 text-sm mb-4">
                This business name is registered. Please verify ownership to update your company name.
              </p>

              <div className="space-y-4">
                <label className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-yellow-300">
                  <input
                    type="checkbox"
                    name="ownsBusiness"
                    checked={verificationData.ownsBusiness}
                    onChange={handleVerificationChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                  />
                  <span className="text-sm text-yellow-800">
                    I confirm that I am the legal owner or authorized representative of this registered business
                  </span>
                </label>

                {verificationData.ownsBusiness && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-yellow-800 mb-2">
                        RC Number *
                      </label>
                      <input
                        type="text"
                        name="rc_number"
                        value={verificationData.rc_number}
                        onChange={handleVerificationChange}
                        className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white"
                        placeholder="Enter your RC number"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-yellow-800 mb-2">
                        Business Certificate *
                      </label>

                      <div className="space-y-3">
                        {!filePreview ? (
                          <div
                            className="border-2 border-dashed border-yellow-300 rounded-lg p-4 text-center cursor-pointer hover:border-yellow-400 transition-colors bg-white"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <svg className="w-8 h-8 text-yellow-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="text-yellow-700 text-sm mb-1">Click to upload business certificate</p>
                            <p className="text-yellow-600 text-xs">PDF, JPG, PNG (Max 10MB)</p>
                          </div>
                        ) : (
                          <div className="bg-white rounded-lg border border-yellow-300 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-yellow-800">Uploaded File</span>
                              <button
                                type="button"
                                onClick={removeFile}
                                className="text-yellow-600 hover:text-yellow-800"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>

                            {filePreview.type === 'pdf' && (
                              <div className="flex items-center space-x-3 p-2 bg-yellow-100 rounded-lg">
                                <div className="w-8 h-10 bg-red-500 rounded flex items-center justify-center">
                                  <span className="text-white text-xs font-bold">PDF</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-yellow-900 truncate">{filePreview.name}</p>
                                  <p className="text-xs text-yellow-700">{formatFileSize(filePreview.size)}</p>
                                </div>
                              </div>
                            )}

                            {filePreview.type === 'image' && (
                              <div className="space-y-2">
                                <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                                  <img
                                    src={filePreview.url}
                                    alt="Preview"
                                    className="w-full h-24 object-contain"
                                  />
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-yellow-800 truncate flex-1 mr-2">{filePreview.name}</span>
                                  <span className="text-xs text-yellow-600 whitespace-nowrap">{formatFileSize(filePreview.size)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <input
                          ref={fileInputRef}
                          type="file"
                          name="business_certificate"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="hidden"
                          required={verificationData.ownsBusiness}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 sm:mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">
                    {formData.address || 'No address added yet'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formData.address
                      ? 'Your business address — updated from the map'
                      : 'Add your business address so buyers can find you'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddressModal(true)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {formData.address ? 'Edit Address' : 'Add Address'}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6 sm:mt-8">
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              disabled={!isFormReady() || isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
          {/* Verification details modal  */}
    <ViewVerificationDetailsModal
    show={showVerificationModal}
    onClose={() => setShowVerificationModal(false)}
    userId={user?.id}
    requestedName={user?.requested_name}
    status={businessdata?.status}
    data={businessdata}
    />

    {/* Address edit modal (same map picker as the login prompt / complete profile) */}
    <AddressPromptModal
      show={showAddressModal}
      initialQuery={user?.company_address || ''}
      onClose={() => setShowAddressModal(false)}
      onSaved={(savedAddress) => {
        setFormData(prev => ({ ...prev, address: savedAddress || '', location_coord: savedAddress ? savedAddress.location_coord : '' }));
      }}
    />
      </div>

      {/* Phone Number Card — standalone, OTP-verified update */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border border-gray-200">
        <h3 className="text-base sm:text-lg font-semibold mb-2">Phone Number</h3>
        <p className="text-gray-500 text-sm mb-4">Your WhatsApp number — used for order notifications and OTP verification</p>

        {phoneStep === 'idle' && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
            <div className="min-w-0">
              <p className="text-sm text-gray-800 truncate">
                {user?.company_phone || 'No phone number added'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {user?.company_phone
                  ? 'Verified WhatsApp number for your account'
                  : 'Add a phone number so buyers can reach you'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setPhoneStep('entering'); setNewPhoneInput(''); setPhoneError(''); }}
              className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              {user?.company_phone ? 'Change Number' : 'Add Number'}
            </button>
          </div>
        )}

        {(phoneStep === 'entering' || phoneStep === 'sending') && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter your new WhatsApp number. We will send a verification code to confirm ownership.
            </p>
            <div className="flex gap-2">
              <select
                value={newCountryCode}
                onChange={(e) => setNewCountryCode(e.target.value)}
                className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors bg-gray-50 text-sm"
              >
                <option value="+234">+234 (NG)</option>
                <option value="+1">+1 (US)</option>
                <option value="+44">+44 (UK)</option>
                <option value="+91">+91 (IN)</option>
                <option value="+254">+254 (KE)</option>
                <option value="+27">+27 (ZA)</option>
                <option value="+233">+233 (GH)</option>
              </select>
              <input
                ref={phoneInputRef}
                type="tel"
                className={`flex-1 px-4 py-2 border rounded-lg transition-colors outline-none ${
                  phoneError
                    ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                style={phoneShake ? { animation: 'phone-shake 0.4s ease-in-out' } : {}}
                placeholder="e.g. 8012345678"
                value={newPhoneInput}
                onChange={(e) => { setNewPhoneInput(e.target.value); setPhoneError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSendPhoneOtp(); }}
                autoFocus
              />
            </div>
            {phoneError && (
              <div className="flex items-center gap-2 text-red-600">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">{phoneError}</p>
              </div>
            )}
            <style>{`
              @keyframes phone-shake {
                0%, 100% { transform: translateX(0); }
                20% { transform: translateX(-6px); }
                40% { transform: translateX(6px); }
                60% { transform: translateX(-4px); }
                80% { transform: translateX(4px); }
              }
            `}</style>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={resetPhoneFlow}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={phoneStep === 'sending'}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendPhoneOtp}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={phoneStep === 'sending'}
              >
                {phoneStep === 'sending' ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </span>
                ) : (
                  'Send OTP'
                )}
              </button>
            </div>
          </div>
        )}

        {(phoneStep === 'otp-sent' || phoneStep === 'verifying') && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter the 6-digit code sent to <span className="font-medium">{newCountryCode}{newPhoneInput}</span>
            </p>
            <div className="flex justify-between items-center px-1">
              <label className="text-sm font-bold text-gray-700">6-digit code</label>
              <button
                type="button"
                className={`text-xs font-bold ${phoneResendCooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500 hover:underline'}`}
                onClick={handleResendPhoneOtp}
                disabled={phoneResendCooldown > 0}
              >
                {phoneResendCooldown > 0 ? `Resend in ${phoneResendCooldown}s` : 'Resend?'}
              </button>
            </div>
            <div className="flex justify-center gap-1.5 sm:gap-2">
              {phoneOtpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={el => phoneOtpInputRefs.current[index] = el}
                  type="text"
                  maxLength="1"
                  className="w-10 h-12 sm:w-14 sm:h-14 text-center bg-gray-50 border border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-xl sm:text-2xl font-bold text-gray-900"
                  value={digit}
                  onChange={(e) => handlePhoneOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handlePhoneOtpKeyDown(index, e)}
                  onPaste={handlePhoneOtpPaste}
                  disabled={phoneStep === 'verifying'}
                />
              ))}
            </div>
            {phoneError && (
              <p className="text-sm text-red-600 text-center">{phoneError}</p>
            )}
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={resetPhoneFlow}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={phoneStep === 'verifying'}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleVerifyPhoneOtp}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={phoneStep === 'verifying'}
              >
                {phoneStep === 'verifying' ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Verifying...
                  </span>
                ) : (
                  'Verify & Update'
                )}
              </button>
            </div>
          </div>
        )}

        {phoneStep === 'success' && (
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-sm text-green-700 font-medium">Phone number updated successfully!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;